import { dateFormatter } from 'helpers/intl';

/**
 * Format a reporting-period key as emitted by the backend's
 * `getReportingPeriodKey` (backend/src/utils/emissions/updateShipmentEmissions.ts):
 * quarterly through 2025, annual from 2026.
 *
 * @param {string} key - e.g. "2025-Q1" or "2026".
 * @returns {string} e.g. "Q1 2025" or "2026".
 */
export const formatPeriodKey = (key) => {
  const [year, quarter] = String(key ?? '').split('-');
  if (!year) return '';
  return quarter ? `${quarter} ${year}` : year;
};

/**
 * Collapse a run of reporting-period keys into a single label. The keys sort
 * lexically into chronological order ("2025-Q4" < "2026"), so the first and
 * last entries bound the range.
 *
 * @param {string[]} periods - Sorted reporting-period keys.
 * @returns {string} "" when empty, "Q1 2025" for one, "Q1 2025 – 2026" for many.
 */
export const formatPeriodRange = (periods) => {
  if (!Array.isArray(periods) || periods.length === 0) return '';
  const first = formatPeriodKey(periods[0]);
  const last = formatPeriodKey(periods[periods.length - 1]);
  return first === last ? first : `${first} – ${last}`;
};

/**
 * Format the reporting period based on date and lastRecordDate
 * @param {string} startDate - The start date (date field)
 * @param {string} endDate - The end date (lastRecordDate field)
 * @param {string|null} granularity - The emission factor granularity
 * @returns {string} Formatted reporting period
 */
const formatReportingPeriod = (startDate, endDate, granularity = null) => {
  if (!startDate || !endDate) return '';
  let isDefaultGranularity = ['Global', 'Country'].includes(granularity);

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const startMonth = start.getMonth(); // 0-11
  const endMonth = end.getMonth();
  const startDay = start.getDate();
  const endDay = end.getDate();

  if (isDefaultGranularity && endYear == 2026) {
    return endYear.toString();
  }

  // Check if it's a full year (Jan 1 to Dec 31 of the same year)
  const isFullYear =
    startMonth === 0 && startDay === 1 && endMonth === 11 && endDay === 31 && startYear === endYear;

  if (isFullYear) {
    return startYear.toString();
  }

  // Check if it's a quarter
  const quarters = [
    { start: { month: 0, day: 1 }, end: { month: 2, day: 31 }, label: 'Q1' },
    { start: { month: 3, day: 1 }, end: { month: 5, day: 30 }, label: 'Q2' },
    { start: { month: 6, day: 1 }, end: { month: 8, day: 30 }, label: 'Q3' },
    { start: { month: 9, day: 1 }, end: { month: 11, day: 31 }, label: 'Q4' },
  ];

  for (const quarter of quarters) {
    const isQuarter =
      startMonth === quarter.start.month &&
      startDay === quarter.start.day &&
      endMonth === quarter.end.month &&
      endDay === quarter.end.day &&
      startYear === endYear;

    if (isQuarter) {
      return `${quarter.label} ${startYear}`;
    }
  }

  // Custom range — locale-aware. Replaces a hand-rolled English month
  // abbreviation array + ordinal suffixes (st/nd/rd/th), which have no correct
  // equivalent in other locales (German writes "21.", French "1er"). Intl
  // renders the right short-date shape per locale — e.g. en-GB "1 Jan 2026",
  // fr-FR "1 janv. 2026". dateFormatter is the memoized Intl.DateTimeFormat
  // from helpers/intl; no locale argument → the active UI locale. Left on local
  // time to match the getMonth()/getDate() basis the full-year and quarter
  // checks above already use.
  const rangeFormat = dateFormatter(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${rangeFormat.format(start)} - ${rangeFormat.format(end)}`;
};

export default formatReportingPeriod;
